/**
 * Droits sur un challenge — RÈGLE UNIQUE, lue par le serveur (`src/api/challenges.ts`)
 * ET par l'écran (`ChallengeSheet`, `ChallengesView`).
 *
 * Pourquoi un seul fichier : la PR #7 affichait « Modifier », « Mettre en pause » et
 * « Terminer » à des profils que le serveur refusait ensuite (403). Décision de Valérie
 * (25/09/2026) : un utilisateur ne voit QUE les gestes qu'il a le droit de faire — donc
 * l'écran et le serveur lisent la même fonction, jamais une copie.
 *
 * Pur : aucune dépendance serveur, importable côté client.
 */
import { challengeEndDatePassed, isChallengeOpen, statusAfterEnd, statusAfterPause, statusAfterResume } from "./challenge-status";

type ChallengeUser = { id: string; profileType: string | null };
type ChallengeRef = { scope?: string | null; createdBy?: string | null };

/** Profils qui pilotent un challenge (création exceptée : COO crée aussi, sans piloter ensuite). */
const PILOT_PROFILES = ["ORG", "ADM", "MNG"] as const;

/**
 * Ce profil pilote-t-il des challenges ? Première garde des routes d'écriture, AVANT de charger le
 * challenge (la règle fine — MNG auteur d'un intra — est `canMngPilotChallenge` / `challengePermissions`).
 * Source unique : la liste était recopiée dix fois dans `src/api/challenges.ts` (audit du lot 4).
 */
export function canPilotChallenges(profileType?: string | null): boolean {
  return !!profileType && (PILOT_PROFILES as readonly string[]).includes(profileType);
}

/**
 * Un MNG ne pilote QUE les challenges intra qu'il a lui-même créés.
 * Pour tout autre profil (ORG/ADM), la garde ne s'applique pas — le contrôle du
 * profil autorisé et l'appartenance au compte restent à la charge de l'appelant.
 *
 * Partagée par tous les handlers d'écriture : la règle y était écrite en plusieurs
 * exemplaires, et deux handlers l'avaient oubliée (31/07/2026).
 */
export function canMngPilotChallenge(user: ChallengeUser, challenge: ChallengeRef): boolean {
  if (user.profileType !== "MNG") return true;
  return challenge.scope === "intra" && challenge.createdBy === user.id;
}

export type ChallengeAction =
  | "edit"          // modifier (brouillon : tout ; en cours : description, fin, seuils)
  | "participants"  // ajouter / retirer / affecter aux équipes
  | "teams"         // créer / renommer / recolorer / supprimer une équipe
  | "activate"
  | "pause"         // en cours → en pause
  | "resume"        // en pause → en cours (date de fin inchangée ; refusée si elle est passée)
  | "end"           // clôturer avant la date (en cours ou en pause)
  | "delete";       // brouillon uniquement, pour tout le monde (ADM compris)

/**
 * Ce que `user` peut faire sur `challenge`. `status` : draft | active | paused | ended.
 *
 * - Pilotage (tout sauf la suppression) : ORG / ADM, et le MNG propriétaire d'un challenge intra
 *   (décision 4.2 : il peut aussi le mettre en pause et le clôturer).
 * - Suppression : un challenge qui a vécu ne se supprime pas — seul un brouillon qui n'a JAMAIS
 *   vécu (`hasLived` faux) part à la corbeille, et seulement par un ORG ou un ADM (décision 4.3,
 *   ADM compris). `hasLived` est calculé par le serveur (`challengesThatLived`).
 */
export function challengePermissions(
  user: ChallengeUser,
  challenge: ChallengeRef & { status?: string | null; hasLived?: boolean; awaitsCampaignDates?: boolean; endDate?: Date | string | null },
  now: Date = new Date(),
): Record<ChallengeAction, boolean> {
  const pilot = !!user.profileType
    && (PILOT_PROFILES as readonly string[]).includes(user.profileType)
    && canMngPilotChallenge(user, challenge);
  const status = challenge.status ?? "draft";
  // En pause, le challenge reste pilotable : on peut prolonger sa date de fin, retoucher
  // ses équipes et ses participants avant de le reprendre (statut réel depuis le 30/09/2026).
  const live = isChallengeOpen(status);
  return {
    edit: pilot && live,
    participants: pilot && live,
    teams: pilot && live,
    // Challenge d'une campagne aux dates « à définir » : on la date d'abord (décision du 29/09/2026).
    activate: pilot && status === "draft" && !challenge.awaitsCampaignDates,
    pause: pilot && statusAfterPause(status) !== null,
    // Date de fin passée pendant la pause : pas de « Reprendre » — on prolonge d'abord (30/09/2026).
    resume: pilot && statusAfterResume(status) !== null && !challengeEndDatePassed(challenge.endDate, now),
    end: pilot && statusAfterEnd(status) !== null,
    // « A vécu » (des parties jouées ou des notifications envoyées) : jamais supprimable. Un
    // challenge en pause n'est pas un brouillon (vrai statut `paused`) : il ne se supprime pas.
    // Les challenges mis en pause avant le 30/09/2026 étaient stockés en `draft` : `hasLived`
    // (trace de lancement) les protège tant qu'ils ne sont pas convertis en `paused`.
    // Inconnu = prudence : sans `hasLived === false` explicite (fourni par le serveur), pas de suppression.
    delete: status === "draft" && challenge.hasLived === false && (user.profileType === "ORG" || user.profileType === "ADM"),
  };
}

/**
 * Le pilote verrait « Reprendre » si la date de fin n'était pas passée : l'écran l'oriente alors vers
 * la modification de la date de fin (autorisée pendant la pause) au lieu d'un geste refusé.
 * Même règle que `challengePermissions(...).resume` (décision du 30/09/2026).
 */
export function resumeBlockedByEndDate(
  user: ChallengeUser,
  challenge: ChallengeRef & { status?: string | null; endDate?: Date | string | null },
  now: Date = new Date(),
): boolean {
  return challengePermissions(user, { ...challenge, endDate: null }, now).resume && challengeEndDatePassed(challenge.endDate, now);
}

/**
 * Le challenge monté POUR une campagne part-il à la corbeille avec elle, quand on supprime
 * ce brouillon de campagne ? Seulement s'il n'a RIEN vécu : brouillon, aucune partie, aucune
 * notification, aucun participant (décision de Valérie, 25/09/2026). Sinon il reste — il est
 * seulement détaché de la campagne supprimée. Le statut seul ne suffit jamais : les challenges mis
 * en pause AVANT le 30/09/2026 étaient stockés en `draft` (la pause a désormais son propre statut
 * `paused`) ; `hasLived` les protège tant qu'ils ne sont pas convertis.
 */
export function challengeLeavesWithCampaign(ch: { status?: string | null; hasLived: boolean; hasParticipants: boolean }): boolean {
  return (ch.status ?? "draft") === "draft" && !ch.hasLived && !ch.hasParticipants;
}
